import { useState, useEffect } from 'react';
import enTranslations from '../locales/en.json';
import trTranslations from '../locales/tr.json';

const translations = {
  en: enTranslations,
  tr: trTranslations
};

/**
 * Custom hook for translations in public menu
 * @param {string} language - Current language code ('en' or 'tr')
 * @returns {function} Translation function
 */
export const usePublicTranslation = (language = 'en') => {
  const [currentLang, setCurrentLang] = useState(language);

  useEffect(() => {
    setCurrentLang(language);
  }, [language]);

  /**
   * Get translation for a key
   * @param {string} key - Translation key in dot notation (e.g., 'publicMenu.searchPlaceholder')
   * @param {object} params - Optional parameters to replace in translation (e.g., {count: 5})
   * @returns {string} Translated text
   */
  const t = (key, params = {}) => {
    const lang = currentLang === 'tr' ? 'tr' : 'en';
    const keys = key.split('.');
    let value = translations[lang];

    // Navigate through nested object
    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        // Fallback to English if translation not found
        value = translations.en;
        for (const fallbackKey of keys) {
          if (value && typeof value === 'object') {
            value = value[fallbackKey];
          } else {
            break;
          }
        }
        break;
      }
    }

    // If still not found, return the key itself
    if (typeof value !== 'string') {
      console.warn(`Translation not found for key: ${key}`);
      return key;
    }

    // Replace parameters in translation
    let result = value;
    Object.keys(params).forEach(param => {
      result = result.replace(new RegExp(`{{${param}}}`, 'g'), params[param]);
    });

    return result;
  };

  return { t, language: currentLang };
};

/**
 * Get translation directly without hook (for non-component usage)
 * @param {string} key - Translation key
 * @param {string} language - Language code
 * @param {object} params - Optional parameters
 * @returns {string} Translated text
 */
export const translate = (key, language = 'en', params = {}) => {
  const lang = language === 'tr' ? 'tr' : 'en';
  const keys = key.split('.');
  let value = translations[lang];

  for (const k of keys) {
    if (value && typeof value === 'object') {
      value = value[k];
    } else {
      value = translations.en;
      for (const fallbackKey of keys) {
        if (value && typeof value === 'object') {
          value = value[fallbackKey];
        } else {
          break;
        }
      }
      break;
    }
  }

  if (typeof value !== 'string') {
    return key;
  }

  let result = value;
  Object.keys(params).forEach(param => {
    result = result.replace(new RegExp(`{{${param}}}`, 'g'), params[param]);
  });

  return result;
};
